import { useMemo } from 'react';

import { ListingType } from 'algolia-api/types/ListingType';
import { Box } from 'components/Box';
import { getHomeUrl } from 'utils/searchHomePages';

import {
  PAGE_HEADER_NAV_MAP,
  PAGE_HEADER_PATH_MAP,
} from './PageHeaderConstants';
import { PageHeaderTopNavLink } from './PageHeaderTopNavLink';
import { usePageHeaderIsActive } from './usePageHeaderIsActive';

type Props = {
  index: number;
  navOption: keyof typeof PAGE_HEADER_NAV_MAP;
  navSection: 'MAIN' | 'STAFF';
  fullSize: boolean;
};

export function PageHeaderMainLink({
  index,
  navOption,
  navSection,
  fullSize,
}: Props) {
  const isSpanishBlog = navOption === 'BLOG' && CURRENT_LOCALE === 'es';
  const isStaff = navSection === 'STAFF';

  const { isActive, navInactive } = usePageHeaderIsActive({ isStaff });

  const linkTo = useMemo(() => {
    if (navOption === 'HOME') return '/';

    if (isStaff) {
      return `/${
        PAGE_HEADER_PATH_MAP[navOption]
          ? PAGE_HEADER_PATH_MAP[navOption]
          : '/staff'
      }`;
    }

    if (isSpanishBlog) return '/es/accion';

    // Grad Schools are the only header link that have a content page
    // Because we want to avoid a redirect after render when we visit a landing page,
    // pass state to avoid landing on the search page
    if (navOption === 'GRAD_PROGRAM') {
      return {
        pathname: getHomeUrl(navOption as ListingType),
        state: { redirectToContent: true },
      };
    }

    if (navOption === 'EVENT') {
      return getHomeUrl(navOption as ListingType, {
        actionType: 'EVENT',
      });
    }

    if (navOption === 'VOLOP') {
      return getHomeUrl(navOption as ListingType, {
        actionType: 'VOLOP',
      });
    }

    return getHomeUrl(navOption as ListingType);
  }, [isSpanishBlog, isStaff, navOption]);

  return (
    <Box height="100%" key={navOption}>
      <PageHeaderTopNavLink
        navSection={navSection}
        index={index}
        linkTo={linkTo}
        isStaff={isStaff}
        type={navOption}
        isActive={isActive}
        navInactive={navInactive}
        fullSize={fullSize}
        external={false}
      />
    </Box>
  );
}
